const GRID_DIVISIONS = 12;
// Use a screen-pixel threshold and convert to world units by current scale
function getWorldSnapThreshold(stageContext) {
  try {
    const scale = stageContext?.providers?.scale?.scale || 1;
    const canvas = stageContext?.context?.canvas;
    const minScreenDim = Math.max(1, Math.min(canvas?.width || 0, canvas?.height || 0));
    const px = Math.max(8, Math.min(24, Math.round(minScreenDim * 0.01)));
    return px / scale;
  } catch {
    return 10; // fallback in world units
  }
}

export function drawFrameGrid(stageContext, node) {
  const { context } = stageContext;
  const { scale, interactive } = stageContext.providers;

  if (!interactive.pointStart && !interactive.movingHandler) return;

  // Only draw grid if frame is selected or has selected children
  const selectedNodes = stageContext.api.getSelectedNodes();
  const hasSelectedChildren = selectedNodes.some((selectedNode) => {
    const ancestors = stageContext.api.getAncenstors(selectedNode.id);
    return ancestors.some((ancestor) => ancestor.id === node.id);
  });

  const isFrameSelected = selectedNodes.some((selectedNode) => selectedNode.id === node.id);

  if (!isFrameSelected && !hasSelectedChildren) {
    return;
  }

  const region = stageContext.api
    .getNodeRegion(node)
    .transform(scale.scale, scale.translateX, scale.translateY);

  // Calculate square grid cell size based on horizontal divisions
  const cellSize = (region.bottomRight.x - region.topLeft.x) / GRID_DIVISIONS;

  // Save context state
  context.save();

  // Set grid line style
  context.strokeStyle = 'rgba(1, 1, 1, 0.5)';
  context.lineWidth = 0.5;
  context.setLineDash([2, 2]);

  // Draw vertical grid lines (based on horizontal divisions)
  for (let i = 0; i <= GRID_DIVISIONS; i++) {
    const x = region.topLeft.x + i * cellSize;
    context.beginPath();
    context.moveTo(x, region.topLeft.y);
    context.lineTo(x, region.bottomRight.y);
    context.stroke();
  }

  // Draw horizontal grid lines using the same square size from top to bottom
  for (let y = region.topLeft.y; y <= region.bottomRight.y; y += cellSize) {
    context.beginPath();
    context.moveTo(region.topLeft.x, y);
    context.lineTo(region.bottomRight.x, y);
    context.stroke();
  }
  // Ensure bottom boundary line is drawn
  if ((region.bottomRight.y - region.topLeft.y) % cellSize !== 0) {
    context.beginPath();
    context.moveTo(region.topLeft.x, region.bottomRight.y);
    context.lineTo(region.bottomRight.x, region.bottomRight.y);
    context.stroke();
  }

  // Restore context state
  context.restore();
}

export function generateGridLines(frame, current, deltaX, deltaY, stageContext) {
  const frameRegion = stageContext.api.getNodeRegion(frame);
  const frameRect = frameRegion.rect;

  const newLeft = current.left + deltaX;
  const newTop = current.top + deltaY;
  const newRight = newLeft + current.width;
  const newBottom = newTop + current.height;
  const newCenterX = newLeft + current.width / 2;
  const newCenterY = newTop + current.height / 2;

  const lines = [];

  // Calculate square grid cell size (unified for move/resize)
  const cellSize = frameRect.w / GRID_DIVISIONS;

  // Calculate grid lines that intersect with the current element
  const startGridX = Math.floor((newLeft - frameRect.x) / cellSize) * cellSize + frameRect.x;
  const endGridX = Math.ceil((newRight - frameRect.x) / cellSize) * cellSize + frameRect.x;
  const startGridY = Math.floor((newTop - frameRect.y) / cellSize) * cellSize + frameRect.y;
  const endGridY = Math.ceil((newBottom - frameRect.y) / cellSize) * cellSize + frameRect.y;

  // Generate vertical grid lines
  for (let x = startGridX; x <= endGridX; x += cellSize) {
    if (x >= frameRect.x && x <= frameRect.x + frameRect.w) {
      lines.push({
        type: 'vertical',
        left: x,
        top: Math.max(frameRect.y, newTop),
        size: Math.min(frameRect.y + frameRect.h, newBottom) - Math.max(frameRect.y, newTop),
        confidence: 0.8,
        isGrid: true,
      });
    }
  }

  // Generate horizontal grid lines
  for (let y = startGridY; y <= endGridY; y += cellSize) {
    if (y >= frameRect.y && y <= frameRect.y + frameRect.h) {
      lines.push({
        type: 'horizontal',
        left: Math.max(frameRect.x, newLeft),
        top: y,
        size: Math.min(frameRect.x + frameRect.w, newRight) - Math.max(frameRect.x, newLeft),
        confidence: 0.8,
        isGrid: true,
      });
    }
  }

  // Generate center alignment grid lines
  // Check vertical center alignment to grid lines
  for (let x = frameRect.x; x <= frameRect.x + frameRect.w; x += cellSize) {
    if (Math.abs(newCenterX - x) < getWorldSnapThreshold(stageContext)) {
      lines.push({
        type: 'vertical',
        left: x,
        top: Math.max(frameRect.y, newTop),
        size: Math.min(frameRect.y + frameRect.h, newBottom) - Math.max(frameRect.y, newTop),
        confidence: 0.9,
        isGrid: true,
        isCenter: true,
      });
    }
  }

  // Check horizontal center alignment to grid lines
  for (let y = frameRect.y; y <= frameRect.y + frameRect.h; y += cellSize) {
    if (Math.abs(newCenterY - y) < getWorldSnapThreshold(stageContext)) {
      lines.push({
        type: 'horizontal',
        left: Math.max(frameRect.x, newLeft),
        top: y,
        size: Math.min(frameRect.x + frameRect.w, newRight) - Math.max(frameRect.x, newLeft),
        confidence: 0.9,
        isGrid: true,
        isCenter: true,
      });
    }
  }

  return lines;
}

export function applyGridSnappingForResize(frame, movingHandler, deltaX, deltaY, stageContext) {
  const frameRegion = stageContext.api.getNodeRegion(frame);
  const frameRect = frameRegion.rect;
  const cacheNode = movingHandler.cache;
  const handleId = movingHandler.id;

  // Get the current node position and size
  const node = stageContext.api.getNode(cacheNode.id);
  const nodeRegion = stageContext.api.getNodeRegion(node);
  const nodeRect = nodeRegion.rect;

  // Calculate the new position and size based on the handle being dragged
  let newLeft = nodeRect.x;
  let newTop = nodeRect.y;
  let newWidth = nodeRect.w;
  let newHeight = nodeRect.h;

  // Apply the delta based on the handle
  switch (handleId) {
    case 'topLeft':
      newLeft = nodeRect.x + deltaX;
      newTop = nodeRect.y + deltaY;
      newWidth = nodeRect.w - deltaX;
      newHeight = nodeRect.h - deltaY;
      break;
    case 'topRight':
      newTop = nodeRect.y + deltaY;
      newWidth = nodeRect.w + deltaX;
      newHeight = nodeRect.h - deltaY;
      break;
    case 'bottomLeft':
      newLeft = nodeRect.x + deltaX;
      newWidth = nodeRect.w - deltaX;
      newHeight = nodeRect.h + deltaY;
      break;
    case 'bottomRight':
      newWidth = nodeRect.w + deltaX;
      newHeight = nodeRect.h + deltaY;
      break;
    case 'top':
      newTop = nodeRect.y + deltaY;
      newHeight = nodeRect.h - deltaY;
      break;
    case 'right':
      newWidth = nodeRect.w + deltaX;
      break;
    case 'bottom':
      newHeight = nodeRect.h + deltaY;
      break;
    case 'left':
      newLeft = nodeRect.x + deltaX;
      newWidth = nodeRect.w - deltaX;
      break;
  }

  // Calculate center points
  const newCenterX = newLeft + newWidth / 2;
  const newCenterY = newTop + newHeight / 2;

  // Calculate grid position relative to frame
  const relativeLeft = newLeft - frameRect.x;
  const relativeTop = newTop - frameRect.y;
  const relativeCenterX = newCenterX - frameRect.x;
  const relativeCenterY = newCenterY - frameRect.y;

  // Calculate square grid cell size (unified for move/resize)
  const cellSize = frameRect.w / GRID_DIVISIONS;

  // Snap to grid (edges)
  const snappedLeft = Math.round(relativeLeft / cellSize) * cellSize;
  const snappedTop = Math.round(relativeTop / cellSize) * cellSize;

  // Snap to grid (centers)
  const snappedCenterX = Math.round(relativeCenterX / cellSize) * cellSize;
  const snappedCenterY = Math.round(relativeCenterY / cellSize) * cellSize;

  // Calculate the snap differences for edges
  const snapDeltaX = snappedLeft - relativeLeft;
  const snapDeltaY = snappedTop - relativeTop;

  // Calculate the snap differences for centers
  const centerSnapDeltaX = snappedCenterX - relativeCenterX;
  const centerSnapDeltaY = snappedCenterY - relativeCenterY;

  // Choose the closest snap (edge or center)
  let finalDeltaX = deltaX;
  let finalDeltaY = deltaY;

  const threshold = getWorldSnapThreshold(stageContext);
  // For X axis: choose edge or center snapping based on which is closer
  if (Math.abs(snapDeltaX) <= threshold && Math.abs(centerSnapDeltaX) <= threshold) {
    if (Math.abs(snapDeltaX) <= Math.abs(centerSnapDeltaX)) {
      finalDeltaX = deltaX + snapDeltaX;
    } else {
      finalDeltaX = deltaX + centerSnapDeltaX;
    }
  } else if (Math.abs(snapDeltaX) <= threshold) {
    finalDeltaX = deltaX + snapDeltaX;
  } else if (Math.abs(centerSnapDeltaX) <= threshold) {
    finalDeltaX = deltaX + centerSnapDeltaX;
  }

  // For Y axis: choose edge or center snapping based on which is closer
  if (Math.abs(snapDeltaY) <= threshold && Math.abs(centerSnapDeltaY) <= threshold) {
    if (Math.abs(snapDeltaY) <= Math.abs(centerSnapDeltaY)) {
      finalDeltaY = deltaY + snapDeltaY;
    } else {
      finalDeltaY = deltaY + centerSnapDeltaY;
    }
  } else if (Math.abs(snapDeltaY) <= threshold) {
    finalDeltaY = deltaY + snapDeltaY;
  } else if (Math.abs(centerSnapDeltaY) <= threshold) {
    finalDeltaY = deltaY + centerSnapDeltaY;
  }

  return {
    deltaX: finalDeltaX,
    deltaY: finalDeltaY,
  };
}

export function applyGridSnappingForRegionResize(
  frame,
  movingHandler,
  deltaX,
  deltaY,
  stageContext,
) {
  const frameRegion = stageContext.api.getNodeRegion(frame);
  const frameRect = frameRegion.rect;
  const cacheNode = movingHandler.cache;
  const handleId = movingHandler.id;

  // Calculate the new position and size based on the handle being dragged
  let newLeft = cacheNode.x;
  let newTop = cacheNode.y;
  let newWidth = cacheNode.w;
  let newHeight = cacheNode.h;

  // Apply the delta based on the handle
  switch (handleId) {
    case 'topLeft':
      newLeft = cacheNode.x + deltaX;
      newTop = cacheNode.y + deltaY;
      newWidth = cacheNode.w - deltaX;
      newHeight = cacheNode.h - deltaY;
      break;
    case 'topRight':
      newTop = cacheNode.y + deltaY;
      newWidth = cacheNode.w + deltaX;
      newHeight = cacheNode.h - deltaY;
      break;
    case 'bottomLeft':
      newLeft = cacheNode.x + deltaX;
      newWidth = cacheNode.w - deltaX;
      newHeight = cacheNode.h + deltaY;
      break;
    case 'bottomRight':
      newWidth = cacheNode.w + deltaX;
      newHeight = cacheNode.h + deltaY;
      break;
    case 'top':
      newTop = cacheNode.y + deltaY;
      newHeight = cacheNode.h - deltaY;
      break;
    case 'right':
      newWidth = cacheNode.w + deltaX;
      break;
    case 'bottom':
      newHeight = cacheNode.h + deltaY;
      break;
    case 'left':
      newLeft = cacheNode.x + deltaX;
      newWidth = cacheNode.w - deltaX;
      break;
  }

  // Calculate center points
  const newCenterX = newLeft + newWidth / 2;
  const newCenterY = newTop + newHeight / 2;

  // Calculate grid position relative to frame
  const relativeLeft = newLeft - frameRect.x;
  const relativeTop = newTop - frameRect.y;
  const relativeCenterX = newCenterX - frameRect.x;
  const relativeCenterY = newCenterY - frameRect.y;

  // Calculate square grid cell size (unified for move/resize)
  const cellSize = frameRect.w / GRID_DIVISIONS;

  // Snap to grid (edges)
  const snappedLeft = Math.round(relativeLeft / cellSize) * cellSize;
  const snappedTop = Math.round(relativeTop / cellSize) * cellSize;

  // Snap to grid (centers)
  const snappedCenterX = Math.round(relativeCenterX / cellSize) * cellSize;
  const snappedCenterY = Math.round(relativeCenterY / cellSize) * cellSize;

  // Calculate the snap differences for edges
  const snapDeltaX = snappedLeft - relativeLeft;
  const snapDeltaY = snappedTop - relativeTop;

  // Calculate the snap differences for centers
  const centerSnapDeltaX = snappedCenterX - relativeCenterX;
  const centerSnapDeltaY = snappedCenterY - relativeCenterY;

  // Choose the closest snap (edge or center)
  let finalDeltaX = deltaX;
  let finalDeltaY = deltaY;

  const threshold = getWorldSnapThreshold(stageContext);
  // For X axis: choose edge or center snapping based on which is closer
  if (Math.abs(snapDeltaX) <= threshold && Math.abs(centerSnapDeltaX) <= threshold) {
    if (Math.abs(snapDeltaX) <= Math.abs(centerSnapDeltaX)) {
      finalDeltaX = deltaX + snapDeltaX;
    } else {
      finalDeltaX = deltaX + centerSnapDeltaX;
    }
  } else if (Math.abs(snapDeltaX) <= threshold) {
    finalDeltaX = deltaX + snapDeltaX;
  } else if (Math.abs(centerSnapDeltaX) <= threshold) {
    finalDeltaX = deltaX + centerSnapDeltaX;
  }

  // For Y axis: choose edge or center snapping based on which is closer
  if (Math.abs(snapDeltaY) <= threshold && Math.abs(centerSnapDeltaY) <= threshold) {
    if (Math.abs(snapDeltaY) <= Math.abs(centerSnapDeltaY)) {
      finalDeltaY = deltaY + snapDeltaY;
    } else {
      finalDeltaY = deltaY + centerSnapDeltaY;
    }
  } else if (Math.abs(snapDeltaY) <= threshold) {
    finalDeltaY = deltaY + snapDeltaY;
  } else if (Math.abs(centerSnapDeltaY) <= threshold) {
    finalDeltaY = deltaY + centerSnapDeltaY;
  }

  return {
    deltaX: finalDeltaX,
    deltaY: finalDeltaY,
  };
}

export function applyGridSnapping(frame, current, deltaX, deltaY, stageContext) {
  const frameRegion = stageContext.api.getNodeRegion(frame);
  const frameRect = frameRegion.rect;

  // Calculate the new position
  const newLeft = current.left + deltaX;
  const newTop = current.top + deltaY;
  const newCenterX = newLeft + current.width / 2;
  const newCenterY = newTop + current.height / 2;

  // Calculate grid position relative to frame
  const relativeLeft = newLeft - frameRect.x;
  const relativeTop = newTop - frameRect.y;
  const relativeCenterX = newCenterX - frameRect.x;
  const relativeCenterY = newCenterY - frameRect.y;

  // Unify to square cell size for move/resize consistency
  const cellSize = frameRect.w / GRID_DIVISIONS;

  // Snap to grid (edge-based)
  const snappedLeft = Math.round(relativeLeft / cellSize) * cellSize;
  const snappedTop = Math.round(relativeTop / cellSize) * cellSize;

  // Snap to grid (center-based)
  const snappedCenterX = Math.round(relativeCenterX / cellSize) * cellSize;
  const snappedCenterY = Math.round(relativeCenterY / cellSize) * cellSize;

  // Calculate the differences
  const snapDeltaX = snappedLeft - relativeLeft;
  const snapDeltaY = snappedTop - relativeTop;
  const centerSnapDeltaX = snappedCenterX - relativeCenterX;
  const centerSnapDeltaY = snappedCenterY - relativeCenterY;

  // Choose the closest snap (edge or center)
  let adjustedDeltaX = deltaX;
  let adjustedDeltaY = deltaY;

  const threshold = getWorldSnapThreshold(stageContext);
  // For X axis: choose edge or center snapping based on which is closer
  if (Math.abs(snapDeltaX) <= threshold && Math.abs(centerSnapDeltaX) <= threshold) {
    if (Math.abs(snapDeltaX) <= Math.abs(centerSnapDeltaX)) {
      adjustedDeltaX = deltaX + snapDeltaX;
    } else {
      adjustedDeltaX = deltaX + centerSnapDeltaX;
    }
  } else if (Math.abs(snapDeltaX) <= threshold) {
    adjustedDeltaX = deltaX + snapDeltaX;
  } else if (Math.abs(centerSnapDeltaX) <= threshold) {
    adjustedDeltaX = deltaX + centerSnapDeltaX;
  }

  // For Y axis: choose edge or center snapping based on which is closer
  if (Math.abs(snapDeltaY) <= threshold && Math.abs(centerSnapDeltaY) <= threshold) {
    if (Math.abs(snapDeltaY) <= Math.abs(centerSnapDeltaY)) {
      adjustedDeltaY = deltaY + snapDeltaY;
    } else {
      adjustedDeltaY = deltaY + centerSnapDeltaY;
    }
  } else if (Math.abs(snapDeltaY) <= threshold) {
    adjustedDeltaY = deltaY + snapDeltaY;
  } else if (Math.abs(centerSnapDeltaY) <= threshold) {
    adjustedDeltaY = deltaY + centerSnapDeltaY;
  }

  // Generate grid lines for visual feedback
  const gridLines = generateGridLines(frame, current, adjustedDeltaX, adjustedDeltaY, stageContext);

  return {
    deltaX: adjustedDeltaX,
    deltaY: adjustedDeltaY,
    lines: gridLines,
  };
}
